import type { MaterializedMenuProduct } from '@iheartjane/data-access';
import {
  doesSpecialApply,
  getSpecialIdsForTagLabel,
  shouldShowGold as showGold,
} from '@iheartjane/menu';
import type { Nullable } from '@iheartjane/types';
import { formatLargeNumber } from '@iheartjane/utils';

import type { Cart } from '@jane/bloom/types/schemas/bloom/checkout';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type {
  ProductApplicableIds,
  StoreSpecials,
} from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { BloomMenuStore } from '@jane/bloom/types/schemas/roots/bloom/menu';
import { getSpecialLabel } from '@jane/bloom/utils/badges/getSpecialLabel';
import { getDefaultProductPhoto } from '@jane/bloom/utils/product/getDefaultProductPhoto';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';

import { formatAvailableWeights } from './formatAvailableWeights';
import { getPercentage } from './getPercentage';
import { getProductPriceLabels } from './getPrice';
import {
  getBaseLabResults,
  getDosageAmountPackInfo,
  getLabResults,
} from './potencyInfo';
import { imageSrcSet } from './productImage';
import { productImageDimensions } from './productImage.constants';
import { REQUIRED_REVIEW_COUNT } from './requiredReviewCount';

type BuildSingleProductSearchResultViewModelArgs = {
  applicablePriceIds?: ProductApplicableIds[] | null;
  bundleTag?: boolean;
  cart?: Nullable<Cart>;
  customLanguage: CustomLanguage;
  janeGold?: JaneGold;
  pinnedStore?: Nullable<BloomMenuStore>;
  /** For PDP pages where this value is already calculated */
  precalculatedShouldShowGold?: boolean;
  product: MaterializedMenuProduct;
  specials: Nullable<StoreSpecials>;
};

export const buildSingleProductSearchResultViewModel = ({
  applicablePriceIds,
  bundleTag,
  cart,
  customLanguage,
  janeGold,
  pinnedStore,
  precalculatedShouldShowGold,
  product,
  specials,
}: BuildSingleProductSearchResultViewModelArgs): ProductSearchResultViewModel => {
  const image_url =
    product.image_urls[0] ||
    getDefaultProductPhoto({
      category: product.category,
      kind: product.kind,
      productRootSubtype: product.root_subtype,
      productType: product.type,
    });

  const applicableSpecial = bundleTag
    ? specials
        ?.filter(Boolean)
        .find((special) =>
          doesSpecialApply(
            getSpecialIdsForTagLabel(product),
            special.id,
            special.special_type
          )
        )
    : undefined;

  const specialLabel = getSpecialLabel({
    bundleTag: Boolean(bundleTag),
    product,
    specials,
    storeComplianceLanguageSettings:
      customLanguage.storeComplianceLanguageSettings,
  });

  const productImageSrcSet = imageSrcSet(
    image_url,
    [productImageDimensions.searchResult.width],
    productImageDimensions.searchResult.width
  );

  const hasProductRating = !!(
    product.aggregate_rating &&
    product.aggregate_rating > 0 &&
    product.review_count &&
    product.review_count >= REQUIRED_REVIEW_COUNT
  );

  const weightsInCart = (cart?.items ?? []).filter(
    (cartItem) => cartItem.productId === product.product_id
  );

  const availableWeightsFormatted = formatAvailableWeights(
    product,
    weightsInCart,
    applicablePriceIds
  );

  const { discountedPriceLabel, priceLabel } = getProductPriceLabels(
    product,
    applicablePriceIds
  );

  const reviewCount =
    product.review_count && product.review_count > 999
      ? formatLargeNumber(product.review_count, 1)
      : product.review_count;

  const specialType =
    applicableSpecial?.special_type ??
    specials?.find((special) => special.id === product.special_id)
      ?.special_type ??
    null;

  const shouldShowGold =
    precalculatedShouldShowGold ??
    Boolean(
      pinnedStore &&
      janeGold &&
      showGold({
        appMode: 'bloom',
        goldConfig: janeGold.config,
        state: pinnedStore.state,
        storeId: pinnedStore.id,
      })
    );

  return {
    ...product,
    aggregateRatingAndCount:
      product.aggregate_rating > 0
        ? `${product.aggregate_rating.toFixed(1)} (${reviewCount})`
        : null,
    availableWeightsFormatted,
    basePotencyLabResults: getBaseLabResults({
      percentCBDA: getPercentage(product.percent_cbda),
      percentTHCA: getPercentage(product.percent_thca),
      primaryPercentCBD: getPercentage(product.percent_cbd),
      primaryPercentTHC: getPercentage(product.percent_thc),
      productPercentTAC: getPercentage(product.percent_tac),
    }),
    discountedPriceLabel,
    dosageAmountPackInfo: getDosageAmountPackInfo(product),
    hasMultipleWeights: Object.keys(availableWeightsFormatted).length > 2, // formatted price includes `each`, so products that have single weight actually have length of 2
    hasReviews: hasProductRating,
    id: Number(product.product_id),
    image_urls: [image_url, ...product.image_urls],
    imageDimensions: productImageDimensions.searchResult,
    imageSrcSet: productImageSrcSet,
    potencyLabResults: getLabResults({
      percentCBDA: getPercentage(product.percent_cbda),
      percentTHCA: getPercentage(product.percent_cbda),
      primaryPercentCBD: getPercentage(product.percent_cbd),
      primaryPercentTHC: getPercentage(product.percent_thc),
      product,
      productPercentTAC: getPercentage(product.percent_tac),
      selectedWeight: Object.keys(availableWeightsFormatted)?.[0],
    }),
    priceLabel,
    shouldShowGold,
    specialLabel,
    specialType,
  };
};
